import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const PillarsContext = createContext(null);

const FALLBACK_SETTINGS = {
  pillar_term_singular: "Pillar",
  pillar_term_plural: "Pillars",
  intro_column_label: "Main Course Introduction",
  cover_title: "The Self-Agency Project",
  cover_mission: "Untouchable. Unshakable. Unstoppable.",
  cover_button_label: "Log in",
  logo_url: null,
  cover_background_url: null,
  cover_background_style: "warm",
  cover_animation: "none",
};

const CACHE_KEY = "sap_settings_cache_v1";

function cachedSettings() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return { ...FALLBACK_SETTINGS, ...JSON.parse(raw) };
  } catch (_) {}
  return null;
}

export function PillarsProvider({ children }) {
  const [pillars, setPillars] = useState([]);
  // Seed from the last-known settings so the real logo shows instantly (no "E" flash).
  const cached = cachedSettings();
  const [settings, setSettings] = useState(cached || FALLBACK_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(!!cached);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const p = await api.get("/pillars");
      setPillars(p.data || []);
    } catch {
      // keep whatever pillars we have; don't wipe them if this call fails
    }
    try {
      const s = await api.get("/settings");
      const data = s.data || FALLBACK_SETTINGS;
      setSettings(data);
      setSettingsLoaded(true);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch (_) {}
    } catch {
      // public endpoint — keep defaults so unauth pages still render
      setSettingsLoaded(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => {
    const bySlug = Object.fromEntries(pillars.map((p) => [p.slug, p]));
    const introLabel = settings.intro_column_label || "Main Course Introduction";
    // Lessons with no pillar belong to the intro/foundational column.
    const labelOf = (slug) => bySlug[slug]?.name || (slug ? slug : introLabel);
    const colorOf = (slug) => bySlug[slug]?.color || "#52525B";
    return {
      pillars, settings, loading, settingsLoaded,
      bySlug, labelOf, colorOf, introLabel,
      term: settings.pillar_term_singular || "Pillar",
      termPlural: settings.pillar_term_plural || "Pillars",
      refresh,
    };
  }, [pillars, settings, loading, settingsLoaded, refresh]);

  return <PillarsContext.Provider value={value}>{children}</PillarsContext.Provider>;
}

export const usePillars = () => {
  const ctx = useContext(PillarsContext);
  if (!ctx) throw new Error("usePillars must be inside <PillarsProvider>");
  return ctx;
};

/** Inline chip style for a pillar by slug — driven from the DB-stored color. */
export function pillarChipStyle(color) {
  const c = color || "#52525B";
  // Convert hex → rgba with alpha for background/border
  const hex = c.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return {
    background: `rgba(${r}, ${g}, ${b}, 0.20)`,
    color: "#000000",
    border: `1px solid rgba(${r}, ${g}, ${b}, 0.45)`,
    fontWeight: 800,
  };
}
